import React, { useEffect, useState } from 'react'
import { Box, Button, Tab, Tabs, Tooltip } from '@material-ui/core'
import { isEmpty } from 'lodash'
import {
  AlertOctagon as AlertOctagonIcon,
  AlertTriangle as AlertTriangleIcon,
  Archive as ArchiveRowIcon,
  Archive as ArchiveIcon,
  Bell as BellIcon,
  BellOff as BellOffIcon,
  CheckSquare as CheckSquareIcon,
  Eye as EyeIcon,
  Feather as FeatherIcon,
  File as FileIcon,
  HelpCircle as HelpCircleIcon,
  Inbox as InboxIcon,
  Info as InfoIcon,
  Layers as LayersIcon,
  Layout as LayoutIcon,
  MessageSquare as MessageSquareIcon,
  Shield as ShieldIcon,
  ShieldOff as ShieldOffIcon,
  ThumbsUp as ThumbsUpIcon,
  User as UserIcon,
} from 'react-feather'
import { Description as DescriptionIcon } from '@material-ui/icons'

import {
  Container,
  ContentHeader,
  DriveTour,
  LoadingFeedback,
  Page,
  PermissionMatrix,
  SVG,
} from 'components'

import helpers from 'helpers'

import useAuth from 'hooks/useAuth'
import useNotificationPreferences from 'hooks/useNotificationPreferences'
import usePermissions from 'hooks/usePermissions'

import constants from 'constants/index'
import useStyles from './styles'

const ROW_ICONS = {
  CheckSquare: <CheckSquareIcon size={20} />,
  User: <UserIcon size={20} />,
  Inbox: <InboxIcon size={20} />,
  Archive: <ArchiveRowIcon size={20} />,
  AlertTriangle: <AlertTriangleIcon size={20} />,
}

// mesmos ícones usados em cada módulo no menu lateral (helpers/auth/mountMenuItems.js)
const AUTOMATIC_ACTIVITIES_MODULE_ICONS = {
  ChangesManagement: <LayoutIcon size={20} />,
  Complaint: <ShieldOffIcon size={20} />,
  ConsentForm: <ThumbsUpIcon size={20} />,
  CookieBanner: <SVG.CookieIcon width={20} height={20} />,
  DataProcess: <LayersIcon size={20} />,
  DataProcessTreatment: <LayersIcon size={20} />,
  GovernanceProject: <DescriptionIcon fontSize="small" />,
  Incident: <AlertOctagonIcon size={20} />,
  MonitoringManagementKpi: <EyeIcon size={20} />,
  MonitoringManagementPmc: <EyeIcon size={20} />,
  PrivacyByDesign: <FeatherIcon size={20} />,
  PrivacyPolicy: <ArchiveIcon size={20} />,
  Questionnaire: <FileIcon size={20} />,
  QuestionControl: <FileIcon size={20} />,
  SolicitationTicket: <MessageSquareIcon size={20} />,
  Ticket: <ShieldIcon size={20} />,
}

const NotificationSettings = () => {
  const {
    AUTOMATIC_ACTIVITIES_COLUMNS,
    AUTOMATIC_ACTIVITIES_NOTIFICATION_EVENTS,
    NOTIFICATION_COLUMNS,
    NOTIFICATION_EVENTS,
    NOTIFICATION_FREQUENCIES,
    NOTIFICATION_DISABLED_HINT,
    NOTIFICATION_ACCENT_COLOR,
    NOTIFICATION_COMPANY_TAB_HINT,
    NOTIFICATION_SCOPE_TABS: TABS,
    NOTIFICATION_SETTINGS_DRIVER_STEP_0,
    NOTIFICATION_SETTINGS_DRIVER_STEP_AUTOMATIC,
    NOTIFICATION_SETTINGS_DRIVER_STEP_COMPANY,
    NOTIFICATION_SETTINGS_DRIVER_STEP_MINE,
    NOTIFICATION_SETTINGS_DRIVER_STEPS_OBJECT,
    SOURCE_ORIGIN_PERMISSION_TAGS,
    automaticPreferenceKeyFor,
  } = constants.activities

  const [tab, setTab] = useState(TABS.MINE)
  const [openTour, setOpenTour] = useState(false)

  const classes = useStyles()
  const { userCompany } = useAuth()
  const { permitted, some, isResponsible, isPmo } = usePermissions()
  const {
    saving,
    loading,
    preferences,
    companyPreferences,
    automaticPreferences,
    handleChange,
    handleCompanyChange,
    handleAutomaticChange,
    handleToggleAllNotifications: toggleAllNotifications,
    handleSave,
  } = useNotificationPreferences({
    NOTIFICATION_FREQUENCIES,
    automaticPreferenceKeyFor,
  })

  const hasModulePermission = (moduleId) => {
    if (moduleId === 'GovernanceProject') {
      return (
        permitted(SOURCE_ORIGIN_PERMISSION_TAGS.GovernanceProject) ||
        Boolean(userCompany?.hasProjectAssignments)
      )
    }

    return permitted(SOURCE_ORIGIN_PERMISSION_TAGS[moduleId])
  }

  const handleToggleAllNotifications = () => {
    const enabling = !hasActiveNotifications

    if (enabling) {
      helpers.mixpanel.track('Atividades', 'Notificação ativada', {
        action: 'atividades_notificacao_ativada',
      })
    } else {
      helpers.mixpanel.track('Atividades', 'Notificação desativada', {
        action: 'atividades_notificacao_desativada',
      })
    }

    toggleAllNotifications({
      tab,
      tabs: TABS,
      enabling,
      allowedAutomaticEvents,
      hasOrganizationTab,
      events: NOTIFICATION_EVENTS,
    })
  }

  const handleOpenTutorial = () => {
    const where = 'Configurações de Notificação'
    const title = 'Tour'

    const params = {
      action: 'tour-notification-settings',
    }

    helpers.mixpanel.track(where, title, params)
    setOpenTour(true)
  }

  const rows = isEmpty(preferences)
    ? []
    : helpers.activities.buildNotificationRows(
        preferences,
        NOTIFICATION_EVENTS,
        NOTIFICATION_FREQUENCIES,
        (event) => ROW_ICONS[event.iconName],
      )
  const companyRows = isEmpty(companyPreferences)
    ? []
    : helpers.activities.buildNotificationRows(
        companyPreferences,
        NOTIFICATION_EVENTS,
        NOTIFICATION_FREQUENCIES,
        (event) => ROW_ICONS[event.iconName],
      )
  const hasOrganizationTab =
    some([
      constants.permissions.KANBAN_TASKS.LIST,
      constants.permissions.KANBAN_TASKS.LIST_BY_DEPARTMENT,
    ]) ||
    isResponsible() ||
    isPmo()

  const allowedAutomaticEvents =
    AUTOMATIC_ACTIVITIES_NOTIFICATION_EVENTS.filter((event) =>
      hasModulePermission(event.id),
    )
  const automaticColumns = [
    AUTOMATIC_ACTIVITIES_COLUMNS[0],
    ...(hasOrganizationTab ? [AUTOMATIC_ACTIVITIES_COLUMNS[1]] : []),
  ]
  const automaticRows = helpers.activities.buildAutomaticNotificationRows(
    automaticPreferences,
    allowedAutomaticEvents,
    (event) => AUTOMATIC_ACTIVITIES_MODULE_ICONS[event.id],
    automaticPreferenceKeyFor,
    hasOrganizationTab,
  )
  const hasAutomaticTab = !isEmpty(allowedAutomaticEvents)

  const tourStepsMatrix = NOTIFICATION_SETTINGS_DRIVER_STEPS_OBJECT.filter(
    (step) => {
      if (step.element === '#' + NOTIFICATION_SETTINGS_DRIVER_STEP_COMPANY) {
        return hasOrganizationTab
      }
      if (step.element === '#' + NOTIFICATION_SETTINGS_DRIVER_STEP_AUTOMATIC) {
        return hasAutomaticTab
      }
      return true
    },
  )

  const rowsByTab = {
    [TABS.MINE]: rows,
    [TABS.COMPANY]: companyRows,
    [TABS.AUTOMATIC]: automaticRows,
  }
  const currentTabRows = rowsByTab[tab]
  const hasActiveNotifications = currentTabRows.some((row) =>
    tab === TABS.AUTOMATIC
      ? Object.values(row.cells).some((cell) => cell.checked)
      : !row.cells.disabled.checked,
  )

  useEffect(() => {
    if (!hasAutomaticTab && tab === TABS.AUTOMATIC) {
      setTab(TABS.MINE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAutomaticTab])

  useEffect(() => {
    if (!hasOrganizationTab && tab === TABS.COMPANY) {
      setTab(TABS.MINE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOrganizationTab])

  return (
    <Page title="Minhas Configurações de Notificação">
      <LoadingFeedback open={loading || saving} />
      <Container maxWidth={false}>
        <ContentHeader title="Minhas Configurações de Notificação">
          <Button
            className={classes.headerButton}
            startIcon={
              hasActiveNotifications ? (
                <BellOffIcon size={18} />
              ) : (
                <BellIcon size={18} />
              )
            }
            onClick={handleToggleAllNotifications}
            variant="outlined"
          >
            {hasActiveNotifications
              ? 'Desativar notificações'
              : 'Ativar notificações'}
          </Button>
          <Button
            id={NOTIFICATION_SETTINGS_DRIVER_STEP_0}
            className={classes.headerButton}
            startIcon={<HelpCircleIcon size={18} />}
            onClick={handleOpenTutorial}
            variant="contained"
            color="primary"
          >
            Tutorial
          </Button>
        </ContentHeader>

        {!loading && (
          <>
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              textColor="primary"
              indicatorColor="primary"
              className={classes.tabs}
            >
              <Tab
                id={NOTIFICATION_SETTINGS_DRIVER_STEP_MINE}
                label="Minhas atividades"
                value={TABS.MINE}
              />
              {hasOrganizationTab && (
                <Tab
                  id={NOTIFICATION_SETTINGS_DRIVER_STEP_COMPANY}
                  label={
                    <Box className={classes.tabLabel}>
                      Da Organização
                      <Tooltip title={NOTIFICATION_COMPANY_TAB_HINT}>
                        <span className={classes.tabInfoIcon}>
                          <InfoIcon size={14} />
                        </span>
                      </Tooltip>
                    </Box>
                  }
                  value={TABS.COMPANY}
                />
              )}
              {hasAutomaticTab && (
                <Tab
                  id={NOTIFICATION_SETTINGS_DRIVER_STEP_AUTOMATIC}
                  label="Atividades Automáticas"
                  value={TABS.AUTOMATIC}
                />
              )}
            </Tabs>

            <Box style={{ display: tab === TABS.MINE ? undefined : 'none' }}>
              <PermissionMatrix
                icon={<BellIcon size={22} />}
                title="Notificações"
                columns={NOTIFICATION_COLUMNS}
                sections={[{ label: 'EMAIL', rows }]}
                disabledHint={NOTIFICATION_DISABLED_HINT}
                onChange={handleChange}
                accentColor={NOTIFICATION_ACCENT_COLOR}
              />
            </Box>

            <Box
              style={{
                display: tab === TABS.COMPANY ? undefined : 'none',
              }}
            >
              <PermissionMatrix
                icon={<BellIcon size={22} />}
                title="Notificações"
                columns={NOTIFICATION_COLUMNS}
                sections={[{ label: 'EMAIL', rows: companyRows }]}
                disabledHint={NOTIFICATION_DISABLED_HINT}
                onChange={handleCompanyChange}
                accentColor={NOTIFICATION_ACCENT_COLOR}
              />
            </Box>

            <Box
              style={{ display: tab === TABS.AUTOMATIC ? undefined : 'none' }}
            >
              <PermissionMatrix
                icon={<BellIcon size={22} />}
                title="Notificações"
                columns={automaticColumns}
                sections={[{ label: 'EMAIL', rows: automaticRows }]}
                onChange={handleAutomaticChange}
                accentColor={NOTIFICATION_ACCENT_COLOR}
              />
            </Box>

            <Box className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
              >
                Salvar
              </Button>
            </Box>
          </>
        )}
      </Container>

      <DriveTour
        stepsMatrix={tourStepsMatrix}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default NotificationSettings
